import React, { useState } from "react";
import Button from "../components/inputs/Button";
import Card from "../components/shared/Card";
import PROJECT_CONFIG from "../config/projectConfig";

const FAQs = () => {
  const [activeCategory, setActiveCategory] = useState("general");
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const faqData = {
    general: [
      {
        id: 1,
        question: `Who can request a Scientific Resolution Panel (${PROJECT_CONFIG.name})?`,
        answer: `A community, Tribe, or political entity that has the authority to adopt and enforce floodplain ordinances for the area under its jurisdiction can request an ${PROJECT_CONFIG.name}. The CEO or authorized community representative must make or endorse the request. The requesting entity must have submitted scientific or technical data within the FEMA 90-day appeal period that was not incorporated into the study in whole or part and request an ${PROJECT_CONFIG.name} to resolve the appeal.`,
      },
      {
        id: 2,
        question: "When can a community request a Scientific Resolution Panel?",
        answer: `A community can request an ${PROJECT_CONFIG.name} if it has submitted scientific or technical data during the 90-day appeal period, and allowed at least 60 days of community consultation with FEMA, with not more than 120 days elapsing since the end of the appeal period. A community that has completed the appeals process and is dissatisfied with FEMA's resolution can request an ${PROJECT_CONFIG.name} within 30 days of a FEMA-issued resolution letter.`,
      },
      {
        id: 3,
        question: "Who is managing the Scientific Resolution Panel?",
        answer: `The ${PROJECT_CONFIG.name} process is managed by the National Institute of Building Sciences (NIBS), a non-profit organization independent from FEMA. NIBS will act as the Panel Sponsor, coordinating the ${PROJECT_CONFIG.name}s, ensuring that proper guidelines and procedures are employed, and maintaining a cadre of experts from which panel members are selected.`,
      },
      {
        id: 4,
        question: "How are panel members selected?",
        answer: `For each ${PROJECT_CONFIG.name} request, a panel of five members is convened. Panel members are technical experts in surface water hydrology, hydraulics, coastal engineering, and other engineering and scientific fields related to the creation of Flood Insurance Rate Maps and Flood Insurance Studies throughout the United States. Based on the technical specifics of the appeal, NIBS develops a list of potential panelists with relevant expertise from its cadre of experts. NIBS also checks that those potential members are available to serve, do not reside in the state from which the appeal is taken, and have no personal or professional interest in its findings of the appeal. NIBS provides the list to the community and FEMA to select the panel members. The community(s) selects a simple majority (three), and FEMA selects the remaining panel members.`,
      },
      {
        id: 5,
        question: "What is the process for requesting a panel?",
        answer: `To request an ${PROJECT_CONFIG.name}, a community, tribe, or other political entity with authority to adopt and enforce floodplain ordinances for the area under its jurisdiction completes an ${PROJECT_CONFIG.name} Request Form (available from FEMA) and submits it to FEMA after at least 60 days of community consultation following the appeal period, though no more than 120 days should have elapsed since the end of the appeal period. The requesting entity must have submitted scientific or technical data within the 90-day appeal period that was not incorporated into the study in whole or part and request an ${PROJECT_CONFIG.name} to resolve the appeal. Once FEMA confirms the appeal is eligible for the ${PROJECT_CONFIG.name} process, it forwards the Scientific Resolution Panel Request form to NIBS to develop a list of potential panelists. Once the Panel is convened, panel members will be provided with a summary of the issue, FEMA's data, and the data the community submitted during the 90-day appeal period. Panel members review the data and on a point-by-point basis, deliberate, and make a decision based on the scientific and technical challenges of the appeal.`,
      },
      {
        id: 6,
        question:
          "Is the recommendation of the Scientific Resolution Panel legally binding?",
        answer:
          "The Panel's decision will become the recommendation to the FEMA Administrator, who will make the final determination and resolve the appeal.",
      },
      {
        id: 7,
        question:
          "What does the Scientific Resolution Panel recommendation include?",
        answer: `Based on the scientific and technical data submitted by the community and FEMA, the Panel will render a written decision that FEMA either deny the community's data or incorporate it in part or in whole into the FIRM. For an appeal to be incorporated, the community's data must at a minimum satisfy NFIP standards for flood hazard mapping. The Panel will present a written report with its decision and rationale to FEMA and the community no later than 90 days after being convened. The ${PROJECT_CONFIG.name}'s decision will become the recommendation to the FEMA Administrator, who will make the final determination. FEMA will then issue a resolution letter.`,
      },
      {
        id: 8,
        question:
          "Can a community present any new data for Scientific Resolution Panel review, outside of the regulatory appeal period?",
        answer: `No, only the data submitted during the FEMA 90-day appeal period can be submitted to an ${PROJECT_CONFIG.name}. For eligibility to request a Scientific Resolution Panel, communities must submit technical or scientific data during the 90-day appeal period. However, FEMA will accept new data at any time through the Letter of Map Revision process for incorporation into revised Flood Insurance Rate Maps.`,
      },
      {
        id: 9,
        question:
          "Can a Scientific Resolution Panel be exercised by FEMA for the resolution of technical or scientific data that is not submitted during the regulatory appeal period of a study?",
        answer: `FEMA may choose to exercise the ${PROJECT_CONFIG.name} process for other scientific needs as deemed appropriate by the agency but this is not the standard practice.`,
      },
    ],
    community: [
      {
        id: 1,
        question:
          "Do I have to have the concurrence of the community to request a Scientific Resolution Panel for my appeal?",
        answer: `Yes. All requests for an ${PROJECT_CONFIG.name} process must be submitted through the impacted community. The community is required to endorse the appeal and agree that all other appeals concerning the community's proposed flood hazard data have been submitted in accordance with regulations.`,
      },
      {
        id: 2,
        question: "Will I be able to pick my own panel members?",
        answer:
          "Yes. The community will be given the opportunity to select the majority of the panelists from a list created by NIBS from its cadre of experts.",
      },
      {
        id: 3,
        question: "Do I have to accept the findings of the panel?",
        answer: `If changes to the maps are recommended in the panel's determination and FEMA elects to implement the panel's determination, FEMA will incorporate the changes into a revised Preliminary FIRM and, if appropriate, FIS report. The revised products will be available to the community for review, with a resolution letter, before FEMA issues an LFD. Once the ${PROJECT_CONFIG.name} provides its determination and FEMA issues its resolution letter to implement the recommendations, the ${PROJECT_CONFIG.name} recommendations are binding on all appellants and not subject to judicial review. If the FEMA Administrator elects not to accept the panel's findings, the Administrator will issue a written justification within 60 days of receiving the report from the ${PROJECT_CONFIG.name}. Under these circumstances, the appellants maintain their right to appeal FEMA's final determination to the appropriate Federal District Court.`,
      },
      {
        id: 4,
        question:
          "Why do I have to wait for 60 days after the appeals period or after negotiations with FEMA transpire before requesting a panel for my issues?",
        answer:
          'Historically the best resolution of conflicts of proposed flood hazard data has been through discussions between parties. This process is referred to as "community consultation". Sixty days of good faith community consultation is required before FEMA will agree to a panel.',
      },
      {
        id: 5,
        question: "Can FEMA request a panel?",
        answer: `FEMA may choose to exercise the ${PROJECT_CONFIG.name} process for other scientific needs as deemed appropriate by the agency but this is not standard practice.`,
      },
      {
        id: 6,
        question: "What will be the submission requirements for a community?",
        answer: `All technical data must have been submitted to FEMA during the 90-day appeal period and FEMA must have denied the appeal in whole or part. To request an ${PROJECT_CONFIG.name}, the CEO or authorized community representative must complete and submit the ${PROJECT_CONFIG.name} Request Form and NIBS Community Submittal Agreement. The community must promptly select the majority of the panelists from a list provided by NIBS during Panel selection process.`,
      },
      {
        id: 7,
        question:
          "Will I have the opportunity to present my case to the panel?",
        answer: `Regulations state that all information for an appeal must be submitted during the 90-day appeal period. However, if you feel your appeal warrants an opportunity to present your data orally to the Panel, you may include a request on the ${PROJECT_CONFIG.name} Request Form. The Panel may also request presentations or clarifications. It is anticipated that no presentations will be made in person, but rather via teleconferencing and web conferencing.`,
      },
    ],
    cadre: [
      {
        id: 1,
        question: `How do I apply to participate as an expert on an ${PROJECT_CONFIG.name}?`,
        answer:
          "Interested persons should review the requirements for panelists and fully complete the application process at https://www.floodsrp.org/application-form.",
      },
      {
        id: 2,
        question: `What will be the compensation for participation on an ${PROJECT_CONFIG.name}?`,
        answer: `${PROJECT_CONFIG.name} Cadre members selected to serve on an ${PROJECT_CONFIG.name} will be paid for each 8 hour day prorated for partial days.`,
      },
      {
        id: 3,
        question: "What kind of data will a panel be reviewing?",
        answer:
          "The data applies directly to the science and engineering that was utilized in the development of the proposed flood hazard data. In accordance with FEMA statute and regulations, an appeal to FEMA's proposed flood hazard data must be data indicating that the hazard data proposed by FEMA are scientifically or technically incorrect. All data to be reviewed by a panel must meet these criteria.",
      },
      {
        id: 4,
        question: "What happens if a panel cannot agree?",
        answer:
          "The recommendation of a panel will be determined by majority vote. However, final written reports may indicate minority opinions.",
      },
      {
        id: 5,
        question: "How much time am I committing to?",
        answer: `The typical ${PROJECT_CONFIG.name} process is estimated to take between the equivalent to 2 to 5 days over the course of 90 calendar days. Applicants must be open to serving on at least one panel per year if selected. Prior to selection potential panelists will be asked to confirm their availability for particular assignments.`,
      },
      {
        id: 6,
        question: "Will travel be required?",
        answer:
          "Travel is not anticipated. Panelists will be provided technical data either electronically or in hard copy. Discussions between panelists are expected to take place by teleconference or web conference.",
      },
      {
        id: 7,
        question:
          "Could I be held personally liable for any results of the recommendation of a panel on which I participate?",
        answer: `All communities submitting an appeal for ${PROJECT_CONFIG.name} review will be required to sign a "hold harmless" agreement releasing the panelists and NIBS from any liability for their findings. Furthermore, the findings of the panels are advisory only, and the FEMA Administrator has the responsibility of making all final determinations of the flood hazard data.`,
      },
      {
        id: 8,
        question: `May I use my participation in the ${PROJECT_CONFIG.name} process on my resume and other similar documents?`,
        answer: `Yes. Once approved as an ${PROJECT_CONFIG.name} Cadre member you may include your membership acceptance as a reference. However, if you do serve on a panel you may not state or discuss the particulars of the panel deliberations. The panel's decision and report will be made publicly available on the ${PROJECT_CONFIG.name} website once submitted to FEMA and the community.`,
      },
    ],
  };

  const handleQuestionClick = (questionId) => {
    if (expandedQuestion === questionId) {
      setExpandedQuestion(null);
    } else {
      setExpandedQuestion(questionId);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: "General Questions",
      community: "Community Member Questions",
      cadre: "Cadre and Panelist Questions",
    };
    return labels[category] || category;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card variant="elevated">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          Frequently Asked Questions (FAQs)
        </h1>

        {/* Category Navigation */}
        <div className="mb-8">
          <div className="flex space-x-6 mb-6">
            {Object.keys(faqData).map((category) => (
              <Button
                key={category}
                variant={activeCategory === category ? "primary" : "outline"}
                onClick={() => setActiveCategory(category)}
                className={`pb-2 border-b-2 rounded-none ${
                  activeCategory === category
                    ? "border-blue-600"
                    : "border-transparent hover:border-blue-300"
                }`}
              >
                {getCategoryLabel(category)}
              </Button>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-6 capitalize">
            {getCategoryLabel(activeCategory)}
          </h2>
        </div>

        {/* FAQ Questions */}
        <div className="space-y-4">
          {faqData[activeCategory].map((faq) => (
            <div key={faq.id} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => handleQuestionClick(faq.id)}
                className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="text-gray-800 font-medium">
                  {faq.question}
                </span>
                <div className="w-6 h-6 border-2 border-blue-400 rounded-full flex items-center justify-center ml-4 flex-shrink-0">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                </div>
              </button>
              {expandedQuestion === faq.id && (
                <div className="px-4 pb-4">
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default FAQs;
